import Navigation from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";
import {
  Target,
  Heart,
  Zap,
  Users,
  TrendingUp,
  Award,
  BookOpen,
  MessageCircle,
} from "lucide-react";

const About = () => {
  const navigate = useNavigate();

  const values = [
    {
      icon: Target,
      title: "Results-Driven",
      description:
        "We focus on practical strategies that deliver measurable outcomes, not just theory.",
    },
    {
      icon: Heart,
      title: "Community First",
      description:
        "Success is better together. We've built a supportive network of entrepreneurs helping each other grow.",
    },
    {
      icon: Zap,
      title: "Action-Oriented",
      description:
        "Less talk, more action. Our framework is designed for immediate implementation.",
    },
    {
      icon: Users,
      title: "Authentic Growth",
      description:
        "We believe in sustainable, ethical business practices that create real value.",
    },
  ];

  const stats = [
    { icon: Users, value: "10,000+", label: "Students Worldwide" },
    { icon: TrendingUp, value: "$50M+", label: "Student Revenue" },
    { icon: Award, value: "4.9/5", label: "Average Rating" },
    { icon: BookOpen, value: "500+", label: "Success Stories" },
  ];

  const team = [
    {
      name: "Lead Instructor",
      role: "Founder & CEO",
      initials: "JD",
      bio: "15+ years building and scaling online businesses. Passionate about helping entrepreneurs achieve financial freedom.",
    },
    {
      name: "Community Manager",
      role: "Head of Community",
      initials: "SK",
      bio: "Expert in building engaged communities and fostering meaningful connections between members.",
    },
    {
      name: "Content Lead",
      role: "Director of Education",
      initials: "MC",
      bio: "Specializes in creating actionable, easy-to-follow curriculum that gets results.",
    },
  ];

  return (
    <>
      <SEOHead
        title="About Us - LaunchToRevenue"
        description="Learn about LaunchToRevenue's mission to help entrepreneurs build million-dollar businesses. Meet our expert team and discover our proven methodology."
        keywords="about LaunchToRevenue, entrepreneurship training, business coaching team, startup mentors"
        url="https://launchpad-to-riches.lovable.app/about"
      />
      <Navigation />
      <main className="min-h-screen pt-16">
      {/* Hero */}
      <section className="py-24 px-4 bg-gradient-to-br from-background via-primary/5 to-background">
        <div className="container mx-auto max-w-4xl text-center space-y-6">
          <h1 className="text-5xl md:text-6xl font-bold tracking-tight">
            Our Mission: Your Success
          </h1>
          <p className="text-xl md:text-2xl text-muted-foreground">
            We're on a mission to help 10,000 entrepreneurs build profitable online
            businesses and achieve financial freedom
          </p>
        </div>
      </section>

      {/* Story */}
      <section className="py-24 px-4">
        <div className="container mx-auto max-w-4xl space-y-8">
          <h2 className="text-4xl font-bold text-center mb-12">Our Story</h2>
          <div className="prose prose-lg max-w-none space-y-6 text-muted-foreground">
            <p className="text-xl font-semibold text-foreground">
              Launch To Revenue didn't start with a business plan — it started with a friendship.
            </p>
            <p>
              Two kids met at a local park — no laptops, no strategy decks, just shared snacks, laughter, and endless imagination. Back then, we weren't talking about entrepreneurship or financial freedom. We were just two kids with big dreams, wondering what life could look like if we ever figured out how to live it on our own terms.
            </p>
            <p>
              Years passed, and life took us down different paths. We grew up, faced real-world responsibilities, and learned what it meant to build something from the ground up. The friendship stayed strong, but the dream of creating something meaningful never really left. We both found ourselves pulled toward the same mission — freedom. The kind that doesn't just change your bank account, but your life.
            </p>
            <p>
              One of us built a staffing company from scratch — generated revenue in under six months, scaled it fast, and sold it for seven figures in less than two years. The other built a self-funded AI automation business — no investors, no shortcuts — helping entrepreneurs simplify their systems, scale smarter, and earn more while working less.
            </p>
            <p>
              Along the way, we both found major success in the digital space — creating and launching multiple online companies and digital products that generated real revenue. We learned that success isn't reserved for people with connections, investors, or huge followings. It's possible for anyone willing to follow the right steps, stay consistent, and build with purpose.
            </p>
            <p>
              But we also saw how unnecessarily complicated the online business world had become. Everywhere we looked, people were being told they needed fancy funnels, viral videos, or massive teams just to make a single sale. The truth is, most people don't fail because they're not capable — they fail because no one ever showed them a simple way to start.
            </p>
            <p>
              That realization became the seed of something bigger. What if we could take everything we'd learned — all the trial, error, wins, and lessons — and create a blueprint that made online success simple and accessible for everyone?
            </p>
            <p className="text-xl font-semibold text-foreground">
              That's how Launch To Revenue was born.
            </p>
            <p>
              It's more than a program. It's a mission. A movement built to help everyday people take control of their time, income, and future — without confusion, burnout, or broken promises.
            </p>
            <p>
              What started as a friendship in a park has turned into a partnership with purpose. Two entrepreneurs who started from scratch, built multiple successful companies, and came together to build something that could help others do the same.
            </p>
            <p className="text-xl font-semibold text-foreground">
              This isn't just our story anymore. It's yours too.
            </p>
            <p>
              Because the truth is — your launch could be the beginning of your own revenue story.
            </p>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-24 px-4 bg-muted/30">
        <div className="container mx-auto max-w-6xl">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <Card key={index} className="border-2 hover:border-primary/30 transition-all">
                <CardContent className="p-6 text-center space-y-3">
                  <stat.icon className="w-10 h-10 mx-auto text-primary" />
                  <div className="text-4xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                    {stat.value}
                  </div>
                  <div className="text-sm text-muted-foreground">{stat.label}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 px-4">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl font-bold text-center mb-16">Our Core Values</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <Card key={index} className="border-2">
                <CardContent className="p-8 space-y-4">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                    <value.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-2xl font-bold">{value.title}</h3>
                  <p className="text-muted-foreground">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-24 px-4 bg-gradient-to-br from-background via-primary/5 to-background">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl font-bold text-center mb-16">Meet the Team</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {team.map((member, index) => (
              <Card key={index} className="border-2 text-center">
                <CardContent className="p-8 space-y-6">
                  <div className="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-3xl font-bold text-primary-foreground">
                    {member.initials}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">{member.name}</h3>
                    <p className="text-primary">{member.role}</p>
                  </div>
                  <p className="text-muted-foreground">{member.bio}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-4">
        <div className="container mx-auto max-w-4xl text-center space-y-8">
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight">
            Ready to Join Us?
          </h2>
          <p className="text-xl text-muted-foreground">
            Become part of a community of ambitious entrepreneurs transforming their
            lives
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="text-lg px-8 py-6 h-auto"
              onClick={() => navigate("/auth")}
            >
              Start Your Journey
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="text-lg px-8 py-6 h-auto"
              onClick={() => navigate("/case-studies")}
            >
              View Success Stories
            </Button>
          </div>
        </div>
      </section>
    </main>
    </>
  );
};

export default About;
